import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  Download, 
  Archive, 
  ChevronDown, 
  ChevronUp, 
  FileText,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react'
import Button from './Button'
import api from '../../utils/api'

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const FileIcon = ({ fileType }) => {
  const iconClass = "w-4 h-4"
  switch (fileType) {
    case 'json':
      return <FileText className={`${iconClass} text-blue-500`} />
    case 'csv':
      return <FileText className={`${iconClass} text-green-500`} />
    case 'excel':
      return <FileText className={`${iconClass} text-green-600`} />
    default:
      return <FileText className={iconClass} />
  }
}

const SingleFileDownload = ({ file }) => {
  const { t } = useTranslation()
  
  return (
    <div className="flex items-center space-x-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.open(file.download_url, '_blank')}
        className="flex items-center space-x-2"
      >
        <Download className="w-4 h-4" />
        <span>{t('translation.download')}</span>
      </Button>
    </div>
  )
}

const MultiFileDownloadSection = ({ files, archiveOptions, onArchiveGenerate, jobId }) => {
  const { t } = useTranslation()
  const [showIndividual, setShowIndividual] = useState(false)
  const [archiveStatus, setArchiveStatus] = useState(null)
  const [archiveTask, setArchiveTask] = useState(null)

  const handleZipDownload = async () => {
    try {
      setArchiveStatus('generating')
      const response = await onArchiveGenerate()
      setArchiveTask(response.task_id)
      
      // Start polling for status
      pollArchiveStatus(response.task_id)
    } catch (error) {
      console.error('Archive generation failed:', error)
      setArchiveStatus('error')
    }
  }

  const pollArchiveStatus = async (taskId) => {
    const maxRetries = 60 // Poll for up to 5 minutes (5s interval)
    let retries = 0
    
    const poll = async () => {
      try {
        // This endpoint should be implemented in the backend
        const response = await api.get(`/user/jobs/${jobId}/archive/status/${taskId}`)
        const status = response.data
        
        setArchiveStatus(status.status)
        
        if (status.status === 'success') {
          setArchiveStatus('ready')
          // Archive is ready, user can download
          if (status.archive_url) {
            window.open(status.archive_url, '_blank')
          }
          return
        } else if (status.status === 'failure') {
          setArchiveStatus('error')
          return
        }
        
        // Continue polling if still in progress
        if (retries < maxRetries && (status.status === 'pending' || status.status === 'progress')) {
          retries++
          setTimeout(poll, 5000) // Poll every 5 seconds
        } else if (retries >= maxRetries) {
          setArchiveStatus('timeout')
        }
      } catch (error) {
        console.error('Error polling archive status:', error)
        setArchiveStatus('error')
      }
    }
    
    // Start polling after a short delay
    setTimeout(poll, 2000)
  }

  const getArchiveButtonText = () => {
    switch (archiveStatus) {
      case 'generating':
        return t('download.archive.generating', 'Generating...')
      case 'ready':
        return t('download.archive.ready', 'Download ZIP')
      case 'error':
        return t('download.archive.error', 'Generation failed')
      case 'timeout':
        return t('download.archive.timeout', 'Timed out - Retry')
      default:
        return t('download.archive.generate', `Download All as ZIP (${formatBytes(archiveOptions?.total_size_bytes)})`)
    }
  }

  const isArchiveButtonDisabled = () => {
    return archiveStatus === 'generating'
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">
          📁 {t('download.multiFile.title', 'Your translation results')} ({files.length} {t('download.multiFile.files', 'files')})
        </h3>
      </div>

      {/* Primary Action: ZIP Download */}
      <div className="flex items-center space-x-2">
        <Button
          variant="default"
          size="sm"
          onClick={handleZipDownload}
          disabled={isArchiveButtonDisabled()}
          className="flex items-center space-x-2"
        >
          {archiveStatus === 'generating' ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : archiveStatus === 'ready' ? (
            <CheckCircle className="w-4 h-4" />
          ) : archiveStatus === 'error' ? (
            <XCircle className="w-4 h-4" />
          ) : (
            <Archive className="w-4 h-4" />
          )}
          <span>{getArchiveButtonText()}</span>
        </Button>

        {archiveStatus === 'generating' && (
          <div className="text-xs text-gray-500 flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            {t('download.archive.estimatedTime', 'Est. {{time}}s', { time: archiveOptions?.estimated_generation_time || 10 })}
          </div>
        )}
      </div>

      {/* Secondary: Individual Files */}
      <details className="border rounded-lg" open={showIndividual}>
        <summary 
          className="px-3 py-2 cursor-pointer select-none flex items-center justify-between text-sm text-gray-600 hover:bg-gray-50"
          onClick={(e) => {
            e.preventDefault()
            setShowIndividual(!showIndividual)
          }}
        >
          <span>{t('download.individual.title', 'Download individual files')}</span>
          {showIndividual ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </summary>
        
        {showIndividual && (
          <div className="p-3 space-y-2 bg-gray-50">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between py-2 px-2 bg-white rounded border">
                <div className="flex items-center space-x-2">
                  <FileIcon fileType={file.file_type} />
                  <span className="text-sm font-medium">{file.filename}</span>
                  {file.language_code && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {file.language_code}
                    </span>
                  )}
                  {file.size_bytes && (
                    <span className="text-xs text-gray-400">
                      ({formatBytes(file.size_bytes)})
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => window.open(file.download_url, '_blank')}
                  className="flex items-center space-x-1"
                >
                  <Download className="w-3 h-3" />
                  <span className="text-xs">{t('download.individual.download', 'Download')}</span>
                </Button>
              </div>
            ))}
          </div>
        )}
      </details>
    </div>
  )
}

const MultiFileDownload = ({ jobResults, onArchiveGenerate }) => {
  const { output_files, archive_options, job_id } = jobResults
  const isMultiFile = output_files && output_files.length > 1

  if (!output_files || output_files.length === 0) {
    return null
  }

  if (!isMultiFile) {
    return <SingleFileDownload file={output_files[0]} />
  }

  return (
    <MultiFileDownloadSection 
      files={output_files} 
      archiveOptions={archive_options}
      onArchiveGenerate={onArchiveGenerate}
      jobId={job_id}
    />
  )
}

export default MultiFileDownload